/**
 * The bulk tagging matrix: Era, Energy, Genre and Rotation for a whole selection
 * in one pass.
 *
 * The top row sets a column for every selected track; any cell below overrides it
 * for one track. What will change is shown before anything is written -- the new
 * value highlighted beside the old -- because a fifty-track edit is exactly where
 * a wrong click is expensive.
 *
 * **Two destinations, on purpose.** Genre is a standard tag, so it is written to
 * the file through SafeTagWriter like every other tag edit, and every player
 * can read it. Era, energy level and rotation are scheduling data
 * that only Nova uses; they live in Nova's custom columns, keyed by payload
 * hash, so setting them opens no file at all. The columns are created the
 * first time they are needed.
 */

import { useMemo, useState } from "react";
import { bulkCustomValues, bulkTag, type CustomColumn, type GenreFacet } from "../api";
import type { Track } from "../types";
import {
  CUSTOM_COLUMNS,
  finalValue,
  plan,
  type CellValue,
  type MatrixColumn,
  type MatrixRow,
} from "./matrixPlan";

const ERAS = ["Pre-1990", "1990s", "2000s", "2010s", "2020s", "Current"];
const ROTATIONS = ["Power", "Heavy", "Medium", "Light", "Recurrent", "Gold"];
const ENERGY = [
  { value: 1, label: "1 · Chill" },
  { value: 2, label: "2 · Easy" },
  { value: 3, label: "3 · Mid" },
  { value: 4, label: "4 · Lift" },
  { value: 5, label: "5 · Peak" },
];

/** Rows rendered in the preview. The write still covers the whole selection. */
const PREVIEW_LIMIT = 300;

const COLS: { key: MatrixColumn; label: string }[] = [
  { key: "era", label: "Decade" },
  { key: "energy_level", label: "Energy" },
  { key: "genre", label: "Genre" },
  { key: "rotation", label: "Group" },
];

function display(v: CellValue | string | number | null): string {
  return v === undefined || v === null || v === "" ? "—" : String(v);
}

function Quick({
  options,
  value,
  onPick,
}: {
  options: { value: string | number; label: string }[];
  value: CellValue;
  onPick: (v: CellValue) => void;
}) {
  return (
    <div className="matrix-quick">
      {options.map((o) => (
        <button
          key={o.value}
          className={`quick ${value === o.value ? "on" : ""}`}
          onClick={() => onPick(value === o.value ? undefined : o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function BulkMatrix({
  tracks,
  columns,
  genres,
  onCreateColumn,
  onClose,
  onDone,
}: {
  /** The selection, in grid order. */
  tracks: Track[];
  columns: CustomColumn[];
  genres: GenreFacet[];
  onCreateColumn: (label: string, type: CustomColumn["type"]) => Promise<string | null>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [bulk, setBulk] = useState<Partial<Record<MatrixColumn, CellValue>>>({});
  const [overrides, setOverrides] = useState<Record<string, Partial<Record<MatrixColumn, CellValue>>>>({});
  const [editing, setEditing] = useState<{ trackId: string; column: MatrixColumn } | null>(null);
  const [running, setRunning] = useState<string | null>(null);
  const [report, setReport] = useState<{ bad: boolean; lines: string[] } | null>(null);
  const [genreText, setGenreText] = useState("");

  const rows: MatrixRow[] = useMemo(
    () =>
      tracks.map((t) => ({
        trackId: t.id,
        current: {
          era: (t.custom?.era as string | null | undefined) ?? null,
          energy_level: (t.custom?.energy_level as number | null | undefined) ?? null,
          genre: t.genre ?? null,
          rotation: (t.custom?.rotation as string | null | undefined) ?? null,
        },
      })),
    [tracks]
  );
  const byId = useMemo(() => new Map(tracks.map((t) => [t.id, t])), [tracks]);
  const p = useMemo(() => plan(rows, bulk, overrides), [rows, bulk, overrides]);
  const total = p.counts.era + p.counts.energy_level + p.counts.genre + p.counts.rotation;

  // A new edit makes the last run's report stale; leaving "saved" on screen above
  // unsaved changes would say the opposite of what is true.
  const setColumn = (column: MatrixColumn, value: CellValue) => {
    setReport(null);
    setBulk((b) => ({ ...b, [column]: value }));
  };

  const setCell = (trackId: string, column: MatrixColumn, value: CellValue) => {
    setReport(null);
    setOverrides((o) => ({ ...o, [trackId]: { ...o[trackId], [column]: value } }));
  };

  const apply = async () => {
    setReport(null);
    const lines: string[] = [];
    let bad = false;

    // Columns first: a missing one would refuse every custom-value request.
    const have = new Set(columns.map((c) => c.key));
    for (const col of CUSTOM_COLUMNS) {
      if (p.counts[col.key] > 0 && !have.has(col.key)) {
        setRunning(`Creating the ${col.label} column…`);
        const error = await onCreateColumn(col.label, col.type);
        if (error) {
          setRunning(null);
          setReport({ bad: true, lines: [`Could not create ${col.label}: ${error}`] });
          return;
        }
      }
    }

    let groupIndex = 0;
    for (const group of p.custom) {
      groupIndex += 1;
      setRunning(`Saving scheduling values (${groupIndex}/${p.custom.length})…`);
      const res = await bulkCustomValues(group.trackIds, group.values);
      if (res.error) {
        bad = true;
        lines.push(res.error);
      } else if (res.failed) {
        bad = true;
        for (const r of res.results ?? []) {
          if (r.status === "failed") lines.push(`${byId.get(String(r.trackId))?.title ?? r.trackId}: ${r.error}`);
        }
      }
    }
    const savedCustom = p.counts.era + p.counts.energy_level + p.counts.rotation;
    if (savedCustom && !bad) lines.push(`Scheduling values saved in Nova for ${savedCustom} cells.`);

    let written = 0;
    for (const group of p.genre) {
      setRunning(`Writing genre to ${group.trackIds.length} file${group.trackIds.length === 1 ? "" : "s"}…`);
      const res = await bulkTag(group.trackIds, { genre: group.genre });
      if (res.error) {
        bad = true;
        lines.push(`Genre: ${res.error}`);
        continue;
      }
      written += res.written ?? 0;
      for (const r of res.results ?? []) {
        if (r.status === "failed" || r.status === "skipped") {
          bad = true;
          lines.push(`${byId.get(String(r.trackId))?.title ?? r.trackId}: ${r.error ?? r.reason ?? r.status}`);
        }
      }
    }
    if (p.counts.genre) lines.push(`Genre written to ${written} of ${p.counts.genre} files, audio verified unchanged.`);

    setRunning(null);
    setReport({ bad, lines });
    setBulk({});
    setOverrides({});
    onDone();
  };

  const preview = rows.slice(0, PREVIEW_LIMIT);

  return (
    <div className="matrix-backdrop" onMouseDown={(e) => e.target === e.currentTarget && !running && onClose()}>
      <div className="matrix" role="dialog" aria-label="Tagging matrix">
        <div className="popover-head">
          <span>
            Tagging matrix · {tracks.length} track{tracks.length === 1 ? "" : "s"}
          </span>
          <button className="icon-button" onClick={onClose} disabled={!!running}>×</button>
        </div>

        <div className="matrix-setters">
          <div className="matrix-setter">
            <span className="matrix-label">Decade</span>
            <Quick
              options={ERAS.map((e) => ({ value: e, label: e }))}
              value={bulk.era}
              onPick={(v) => setColumn("era", v)}
            />
            <button className={`quick clear ${bulk.era === null ? "on" : ""}`} onClick={() => setColumn("era", bulk.era === null ? undefined : null)} title="Clear for all">∅</button>
          </div>
          <div className="matrix-setter">
            <span className="matrix-label">Energy</span>
            <Quick options={ENERGY} value={bulk.energy_level} onPick={(v) => setColumn("energy_level", v)} />
            <button className={`quick clear ${bulk.energy_level === null ? "on" : ""}`} onClick={() => setColumn("energy_level", bulk.energy_level === null ? undefined : null)} title="Clear for all">∅</button>
          </div>
          <div className="matrix-setter">
            <span className="matrix-label">Genre</span>
            <input
              className="matrix-input"
              list="matrix-genres"
              placeholder="Type or pick a genre…"
              value={genreText}
              onChange={(e) => {
                setGenreText(e.target.value);
                setColumn("genre", e.target.value.trim() ? e.target.value : undefined);
              }}
            />
            <datalist id="matrix-genres">
              {genres.map((g) => (
                <option key={g.name} value={g.name} />
              ))}
            </datalist>
            <span className="matrix-note">written to the file tag</span>
          </div>
          <div className="matrix-setter">
            <span className="matrix-label">Group</span>
            <Quick
              options={ROTATIONS.map((r) => ({ value: r, label: r }))}
              value={bulk.rotation}
              onPick={(v) => setColumn("rotation", v)}
            />
            <button className={`quick clear ${bulk.rotation === null ? "on" : ""}`} onClick={() => setColumn("rotation", bulk.rotation === null ? undefined : null)} title="Clear for all">∅</button>
          </div>
        </div>

        <div className="matrix-table-wrap">
          <table className="matrix-table">
            <thead>
              <tr>
                <th>Track</th>
                {COLS.map((c) => (
                  <th key={c.key}>
                    {c.label}
                    {p.counts[c.key] > 0 && <span className="matrix-count">{p.counts[c.key]}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.map((row) => {
                const t = byId.get(row.trackId);
                return (
                  <tr key={row.trackId}>
                    <td className="matrix-track" title={t?.path}>
                      {t?.title} <em>{t?.artist}</em>
                    </td>
                    {COLS.map(({ key }) => {
                      const next = finalValue(row, key, bulk, overrides);
                      const current = row.current[key];
                      const changes = next !== undefined && display(next) !== display(current);
                      const isEditing = editing?.trackId === row.trackId && editing.column === key;
                      return (
                        <td
                          key={key}
                          className={`matrix-cell ${changes ? "changes" : ""} ${
                            overrides[row.trackId]?.[key] !== undefined ? "override" : ""
                          }`}
                          onClick={() => !isEditing && setEditing({ trackId: row.trackId, column: key })}
                          title="Click to set this track alone"
                        >
                          {isEditing ? (
                            <input
                              autoFocus
                              // Typing replaces the value, as in a spreadsheet cell.
                              onFocus={(e) => e.currentTarget.select()}
                              className="matrix-cell-input"
                              defaultValue={next === undefined || next === null ? display(current).replace("—", "") : String(next)}
                              onBlur={(e) => {
                                const text = e.target.value.trim();
                                const value: CellValue =
                                  text === ""
                                    ? null
                                    : key === "energy_level"
                                      ? Number(text)
                                      : text;
                                if (key === "energy_level" && text !== "" && !Number.isFinite(value as number)) {
                                  setEditing(null);
                                  return;
                                }
                                setCell(row.trackId, key, display(value) === display(current) && bulk[key] === undefined ? undefined : value);
                                setEditing(null);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                                if (e.key === "Escape") {
                                  e.stopPropagation();
                                  setEditing(null);
                                }
                              }}
                            />
                          ) : changes ? (
                            <>
                              <s>{display(current)}</s> <b>{display(next)}</b>
                            </>
                          ) : (
                            display(current)
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.length > PREVIEW_LIMIT && (
            <div className="matrix-more">
              Showing the first {PREVIEW_LIMIT} of {rows.length}. The column values above apply to all{" "}
              {rows.length}.
            </div>
          )}
        </div>

        <div className="popover-foot matrix-foot">
          <div className={`matrix-report ${report?.bad ? "bad" : ""}`}>
            {running ??
              (report
                ? report.lines.slice(0, 4).join(" · ") + (report.lines.length > 4 ? ` · +${report.lines.length - 4} more` : "")
                : total === 0
                  ? "Pick values above, or click a cell to set one track."
                  : `${total} change${total === 1 ? "" : "s"} across ${tracks.length} tracks · ${p.custom.length + p.genre.length} request${p.custom.length + p.genre.length === 1 ? "" : "s"}`)}
          </div>
          <button className="ghost" onClick={onClose} disabled={!!running}>
            {report ? "Close" : "Cancel"}
          </button>
          <button className="primary" disabled={total === 0 || !!running} onClick={() => void apply()}>
            {running ? "Applying…" : `Apply ${total || ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
